import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, firstValueFrom, map, Observable } from 'rxjs';
import { ConversationChannel, Participant, ParticipantType } from '@vendasta/conversation';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_PLATFORM_LOCATION_TOKEN,
  PARTNER_ID_TOKEN,
  USER_ID_TOKEN,
} from '../tokens';
import { toFirestoreId } from '../conversation-utils';
import { ConversationService } from './conversation.service';
import { ConversationStatelessService } from '../conversation-stateless.service';
import { v4 as uuidv4 } from 'uuid';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Injectable()
export class AiAssistantConversationService {
  private readonly conversationService = inject(ConversationService);
  private readonly conversationStatelessService = inject(ConversationStatelessService);
  private readonly snackbarService = inject(SnackbarService);

  private readonly userId$ = inject(USER_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly senderParticipant$: Observable<Participant>;
  private readonly platformLocation = inject(CONVERSATION_PLATFORM_LOCATION_TOKEN);

  private readonly aiAssistantId$$ = new BehaviorSubject<string | null>(null);
  readonly aiAssistantId$ = this.aiAssistantId$$.pipe(distinctUntilChanged());

  constructor() {
    this.senderParticipant$ = combineLatest([this.userId$, this.accountGroupId$, this.partnerId$]).pipe(
      map(([user, agid, partnerID]) => {
        return new Participant({
          participantType: ParticipantType.PARTICIPANT_TYPE_IAM_USER,
          partnerId: partnerID,
          accountGroupId: agid,
          internalParticipantId: user,
          isSubjectParticipant: true,
        });
      }),
    );
  }

  setAssistantId(assistantId: string | null): void {
    this.aiAssistantId$$.next(assistantId);
  }

  async startConversation(): Promise<void> {
    const assistantId = await firstValueFrom(this.aiAssistantId$);
    if (!assistantId) {
      this.snackbarService.openErrorSnack('INBOX.ERROR.UNABLE_TO_CREATE_CONVERSATION');
      return;
    }

    const sender = await firstValueFrom(this.senderParticipant$);
    const partnerId = await firstValueFrom(this.partnerId$);
    const agid = await firstValueFrom(this.accountGroupId$);

    const aiParticipant = new Participant({
      participantType: ParticipantType.PARTICIPANT_TYPE_AI_ASSISTANT,
      partnerId: partnerId,
      accountGroupId: agid,
      internalParticipantId: assistantId,
      isSubjectParticipant: true,
    });

    try {
      const resp = await firstValueFrom(
        this.conversationStatelessService.createConversation(
          [sender, aiParticipant],
          ConversationChannel.CONVERSATION_CHANNEL_AI_ASSISTANT,
          this.platformLocation,
          uuidv4(),
        ),
      );

      this.conversationService.setCurrentFirestoreConversationId(
        toFirestoreId(resp?.conversation?.conversationId ?? ''),
      );
    } catch (error) {
      this.snackbarService.openErrorSnack('INBOX.ERROR.UNABLE_TO_CREATE_CONVERSATION');
    }
  }
}
